import { useMemo } from 'react';
import { Upload } from 'lucide-react';
import type { UploadLinkConfig } from '@peles/shared';
import { UPLOAD_FILE_TYPES } from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useAppSettingLists } from '../../hooks/useAppSettings';

/** An item of the office's `document_type` list (Settings → App Setting Lists). */
interface DocumentTypeItem {
  value: string;
  label: string;
}

/**
 * "Ask for a file back", with somewhere to put it and something to expect
 * (COMMUNICATIONS-DESIGN C7).
 *
 * ⚠️ **Built once for every composer, not per feature.** The vendor email had
 * this as a bare checkbox and nothing else could reach it — which is why the
 * feature existed for months without being findable. It drops into
 * `ComposeEmailModal`'s `extraOptions` slot, so any email gains it by passing
 * this rather than by growing its own.
 *
 * ⚠️ **A folder is not required.** An email about a record can attach the file
 * to that record and file it nowhere in particular, exactly as before; a folder
 * only adds where it lands. What is required is that at least *one* of the two
 * exists, and the caller — not this component — owns the record half, so the
 * server refuses the empty case rather than this trying to.
 */
export interface UploadLinkOptionsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  config: UploadLinkConfig;
  onConfigChange: (config: UploadLinkConfig) => void;
  /** True when the email is about a record, so a folder is genuinely optional. */
  hasRecordTarget?: boolean;
  /**
   * The library folders the file may be filed in, `{ id, path }` — the record's
   * own (`/threads/record-folders`), resolved by the caller. Since Phase 8 there
   * is one file system, so the email and the conversation offer the same list.
   */
  folders: { id: string; path: string }[];
  /** Whether that list is still being fetched. */
  foldersLoading?: boolean;
  disabled?: boolean;
}

/**
 * The destination and the accepted types, without the checkbox in front of them.
 *
 * ⚠️ **Extracted so a second caller cannot re-implement the seeding rule.** The
 * "which folder" question is the same wherever it is asked, and the interesting
 * part is not the input — it is the note below about re-seeding when the record
 * changes, which took a bug to find. A conversation asking for a file back
 * needs those fields and does *not* need a checkbox saying whether to ask,
 * because a dialog called "Ask for a file back" has already answered that. A
 * copy of these two inputs there would have been a copy of the seeding rule
 * too, and the copy is what goes stale.
 *
 * ⚠️ **Mounting is the arming.** `UploadLinkOptions` renders this only when its
 * checkbox is on, so unmounting resets the "which record did I seed for" ref
 * exactly as switching the option off used to — one mechanism rather than a
 * flag and a lifecycle saying the same thing.
 */
export interface UploadLinkFieldsProps {
  config: UploadLinkConfig;
  onConfigChange: (config: UploadLinkConfig) => void;
  hasRecordTarget?: boolean;
  /**
   * The library folders to offer, ordered root-first.
   *
   * ⚠️ **Never seeded as the default.** Choosing nothing leaves `folderId`
   * null: a conversation's file then lands in its record's `Conversations`
   * folder, and an email's on the record only — where each belongs.
   */
  folders: { id: string; path: string }[];
  /**
   * Whether that list is still being fetched.
   *
   * Only the empty-state line depends on it: an empty array while a walk is in
   * flight is not "this record has no folders yet", and saying so and then
   * filling the dropdown a moment later is worse than saying nothing.
   */
  foldersLoading?: boolean;
  disabled?: boolean;
}

export function UploadLinkFields({
  config,
  onConfigChange,
  hasRecordTarget = false,
  folders: options,
  foldersLoading = false,
  disabled = false,
}: UploadLinkFieldsProps) {
  /*
   * What the file is — COI, Contract — from the office's own list, so a type
   * added in Settings is offered here without a deploy. The arriving file is
   * tagged with it wherever it lands: the conversation, the vendor's row, the
   * library.
   */
  const { data: settingLists = [] } = useAppSettingLists();
  const documentTypes = useMemo(
    () =>
      (
        (settingLists.find((list) => list.listCode === 'document_type')?.items as
          | DocumentTypeItem[]
          | undefined) ?? []
      ).filter((item) => typeof item?.value === 'string' && item.value !== ''),
    [settingLists],
  );

  return (
    <div className="space-y-3" data-testid="upload-link-config">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          File it in{hasRecordTarget ? ' (optional)' : ''}
        </label>
        <SearchableListSelect
          items={options}
          value={config.folderId ?? null}
          onChange={(value) =>
            onConfigChange({
              ...config,
              folderId: typeof value === 'string' && value ? value : null,
            })
          }
          getValue={(option) => option.id}
          getLabel={(option) => option.path}
          placeholder={
            // Naming where it actually goes, rather than leaving a conversation's
            // sender to guess what "the record only" means for a file.
            hasRecordTarget ? 'The record’s own files' : 'Choose a folder…'
          }
          disabled={disabled}
        />
        {/*
          Said out loud, because "No options" inside a closed dropdown is not an
          explanation — it was the entire visible symptom of the picker being
          pointed at the wrong file system.
        */}
        {options.length === 0 && !foldersLoading && (
          <p className="mt-1 text-xs text-gray-500" data-testid="no-scoped-folders">
            This record has no folders yet.
          </p>
        )}
        {!hasRecordTarget && !config.folderId && (
          <p className="mt-1 text-xs text-amber-600" data-testid="folder-required">
            Choose a folder so the file has somewhere to go.
          </p>
        )}
      </div>

      {documentTypes.length > 0 && (
        <div>
          <label
            htmlFor="upload-link-document-type"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            Tag the file as (optional)
          </label>
          <SearchableListSelect
            inputId="upload-link-document-type"
            items={documentTypes}
            value={config.documentType ?? null}
            onChange={(value) =>
              onConfigChange({
                ...config,
                documentType: typeof value === 'string' && value ? value : null,
              })
            }
            getValue={(type) => type.value}
            getLabel={(type) => type.label}
            placeholder="No tag"
            disabled={disabled}
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Accept (leave empty for any file)
        </label>
        <SearchableListSelect
          items={[...UPLOAD_FILE_TYPES]}
          value={config.fileTypes ?? []}
          onChange={(value) =>
            onConfigChange({ ...config, fileTypes: Array.isArray(value) ? value : [value] })
          }
          getValue={(type) => type.key}
          getLabel={(type) => type.label}
          getSubtitle={(type) => type.extensions.join(' ')}
          placeholder="Any file"
          multiple
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function UploadLinkOptions({
  enabled,
  onEnabledChange,
  config,
  onConfigChange,
  hasRecordTarget = false,
  folders,
  foldersLoading = false,
  disabled = false,
}: UploadLinkOptionsProps) {
  return (
    <div className="space-y-3">
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          data-testid="include-upload-link"
        />
        <span>
          <span className="flex items-center gap-1 font-medium">
            <Upload size={13} />
            Ask for a file back
          </span>
          <span className="text-xs text-gray-500">
            Adds a button each recipient can use to send you a file — no login needed.
          </span>
        </span>
      </label>

      {enabled && (
        <div className="pl-6">
          <UploadLinkFields
            config={config}
            onConfigChange={onConfigChange}
            hasRecordTarget={hasRecordTarget}
            folders={folders}
            foldersLoading={foldersLoading}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
